import { commonUtils } from '../common_utils';

// Helper for IndexedDB operations
const DB_VERSION = 1;
const SCHEMA_VERSION = 1;
const dbHelper = {
    get dbName() {
        const TAB_ID_KEY = 'tabDbId';
        if (!sessionStorage.getItem(TAB_ID_KEY)) {
            sessionStorage.setItem(TAB_ID_KEY, `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`);
        }
        return `${this.tabPrefix}${sessionStorage.getItem(TAB_ID_KEY)}`;
    },
    storeName: 'search-filters',
    tabPrefix: 'search-tab-',

    // Initialize the database
    initDB() {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName, DB_VERSION);
            request.onupgradeneeded = function(event) {
                const db = event.target.result;
                if (!db.objectStoreNames.contains(dbHelper.storeName)) {
                    db.createObjectStore(dbHelper.storeName);
                }

                // Create a meta store with creation timestamp
                if (!db.objectStoreNames.contains('__meta__')) {
                    const metaStore = db.createObjectStore('__meta__');
                    metaStore.transaction.oncomplete = () => {
                        const tx = db.transaction('__meta__', 'readwrite');
                        const store = tx.objectStore('__meta__');
                        const now = Date.now();
                        store.put(now, 'createdAt');
                        store.put(now, 'lastUpdated');
                        store.put(SCHEMA_VERSION, 'schemaVersion');
                    };
                }
            };

            request.onsuccess = function(event) {
                resolve(event.target.result);
            };
            request.onerror = function(event) {
                reject(event.target.error);
            };
        });
    },

    // Get data from the store
    getData(key) {
        return this.initDB().then(
            (db) =>
                new Promise((resolve, reject) => {
                    const transaction = db.transaction([this.storeName], 'readonly');
                    const store = transaction.objectStore(this.storeName);
                    const request = store.get(key);
                    request.onsuccess = function() {
                        resolve(request.result || {});
                    };
                    request.onerror = function() {
                        reject(request.error);
                    };
                }),
        );
    },

    // Save data to the store
    saveData(key, value) {
        return this.initDB().then(
            (db) =>
                new Promise((resolve, reject) => {
                    const transaction = db.transaction([this.storeName], 'readwrite');
                    const store = transaction.objectStore(this.storeName);
                    const request = store.put(value, key);
                    request.onsuccess = () => {
                        this.updateMetaTimestamp().then(resolve).catch(reject);
                    };
                    request.onerror = () => reject(request.error);
                }),
        );
    },

    // Delete data from the store
    deleteData(key) {
        return this.initDB().then(
            (db) =>
                new Promise((resolve, reject) => {
                    const transaction = db.transaction([this.storeName], 'readwrite');
                    const store = transaction.objectStore(this.storeName);
                    const request = store.delete(key);
                    request.onsuccess = () => {
                        this.updateMetaTimestamp().then(resolve).catch(reject);
                    };
                    request.onerror = () => reject(request.error);
                }),
        );
    },
    updateMetaTimestamp() {
        return this.initDB().then(
            (db) =>
                new Promise((resolve, reject) => {
                    const tx = db.transaction('__meta__', 'readwrite');
                    const store = tx.objectStore('__meta__');
                    const request = store.put(Date.now(), 'lastUpdated');
                    request.onsuccess = () => resolve();
                    request.onerror = () => reject(request.error);
                }),
        );
    },
};

export const SEARCH_STORAGE_KEY = 'params';
export const searchFormStorage = {
    // Key for storing form data in IndexedDB
    storageKey: SEARCH_STORAGE_KEY,

    // Get all form data from IndexedDB
    getFormData() {
        return dbHelper.getData(this.storageKey);
    },

    // Save all form data to IndexedDB
    saveFormData(data) {
        const dataWithTimestamp = {
            ...data,
            _timestamp: Date.now(),
        };
        return dbHelper.saveData(this.storageKey, dataWithTimestamp);
    },

    // Update specific field in IndexedDB
    async updateField(fieldName, value) {
        const data = await this.getFormData();
        data[fieldName] = value;
        return this.saveFormData(data);
    },

    // Delete specific field in IndexedDB
    async deleteField(fieldName) {
        const data = await this.getFormData();
        if (Object.hasOwn(data, fieldName)) {
            delete data[fieldName];
            return this.saveFormData(data);
        }
        return undefined;
    },

    // Reset specific field in IndexedDB
    async resetField(fieldName) {
        return this.deleteField(fieldName);
    },

    // Reset multiple fields in IndexedDB
    async resetFields(fieldNames) {
        const data = await this.getFormData();
        fieldNames.forEach(function(fieldName) {
            if (Object.hasOwn(data, fieldName)) {
                delete data[fieldName];
            }
        });
        return this.saveFormData(data);
    },

    // Reset all form data in IndexedDB
    resetAll() {
        return dbHelper.deleteData(this.storageKey);
    },

    // Delete a key
    async removeKey(keyName) {
        await dbHelper.deleteData(keyName);
    },
    async isSearchEmpty() {
        const data = await this.getFormData();
        // If store is empty, return true
        if (!data || typeof data !== 'object') {
            return true;
        }

        // If object has no keys, it's empty
        if (Object.keys(data).length === 0) {
            return true;
        }

        // Check if all relevant keys have empty values
        for (const key of Object.keys(data)) {
            // Skip keys that are not relevant for search
            if (
                key === '_timestamp' ||
                (key === 'statusType' && data[key] === 'forsale') ||
                (key === 'includeMakeModel' && data[key] === true) ||
                (key === 'adType' && commonUtils.isNettikaravaani() && data[key] === 'forsale')
            ) {
                continue;
            }

            const value = data[key];

            // If value exists and is not empty, return false
            if (value !== null && value !== undefined && value !== '') {
                // For arrays and objects, check if they're empty
                if (typeof value === 'object') {
                    if (Array.isArray(value)) {
                        if (value.length > 0) {
                            return false;
                        }
                    } else if (Object.keys(value).length > 0) {
                        return false;
                    }
                } else {
                    // For primitive values, if they exist, return false
                    return false;
                }
            }
        }

        // If we get here, all values were empty
        return true;
    },

    // Cleanup old databases if older than maxAge (in milliseconds), default is 7 days
    async cleanupOldDatabases(maxAgeMs = 7 * 24 * 60 * 60 * 1000) {
        if (!indexedDB.databases) {
            console.warn('Browser does not support indexedDB.databases()');
            return;
        }

        const now = Date.now();
        const databases = await indexedDB.databases();

        for (const db of databases) {
            if (!db.name || !db.name.startsWith(dbHelper.tabPrefix)) {
                continue;
            }

            const request = indexedDB.open(db.name);
            request.onsuccess = (event) => {
                const database = event.target.result;
                const hasMeta = database.objectStoreNames.contains('__meta__');
                const hasStore = database.objectStoreNames.contains(dbHelper.storeName);
                if (!hasMeta && !hasStore) {
                    database.close();
                    return;
                }

                const tx = database.transaction(hasMeta ? '__meta__' : dbHelper.storeName, 'readonly');
                const store = tx.objectStore(hasMeta ? '__meta__' : dbHelper.storeName);
                const storeRequest = store.get(hasMeta ? 'lastUpdated' : 'params');

                storeRequest.onsuccess = () => {
                    const { result } = storeRequest;
                    let lastUpdated = 0;

                    if (hasMeta && typeof result === 'number') {
                        lastUpdated = result;
                    } else if (result && typeof result._timestamp === 'number') {
                        lastUpdated = result._timestamp;
                    }

                    if (!lastUpdated || now - lastUpdated > maxAgeMs) {
                        console.log(`Deleting stale IndexedDB: ${db.name}`);
                        database.close();
                        indexedDB.deleteDatabase(db.name);
                    } else {
                        database.close();
                    }
                };

                storeRequest.onerror = () => database.close();
            };
        }
    },
};
